import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR, MODE_PAIEMENT_LABELS } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Plus, Trash2, Pencil, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);
const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const emptyForm = {
  date: '', montant: 0, libelle: '', mode_paiement: 'virement',
  facture_id: '', projet_id: '', promoteur_id: '', reference: '', description: '',
};

const MODE_COLORS = {
  virement: 'border-blue-300 bg-blue-50 text-[#002FA7]',
  cheque: 'border-violet-300 bg-violet-50 text-violet-700',
  especes: 'border-amber-300 bg-amber-50 text-amber-700',
  carte: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  prelevement: 'border-indigo-300 bg-indigo-50 text-indigo-700',
  autre: 'border-zinc-300 bg-zinc-50 text-zinc-700',
};

export default function Encaissements() {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [moisFilter, setMoisFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [projetFilter, setProjetFilter] = useState('all');
  const [encaissements, setEncaissements] = useState([]);
  const [projets, setProjets] = useState([]);
  const [promoteurs, setPromoteurs] = useState([]);
  const [factures, setFactures] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [e, p, c, f] = await Promise.all([
      api.get(`/encaissements?annee=${annee}`),
      api.get('/projets'),
      api.get('/promoteurs'),
      api.get(`/paiements?annee=${annee}`),
    ]);
    setEncaissements(e.data);
    setProjets(p.data);
    setPromoteurs(c.data);
    setFactures(f.data);
  };

  useEffect(() => { load(); }, [annee]);

  const projMap = useMemo(() => Object.fromEntries(projets.map(p => [p.id, p])), [projets]);
  const promoMap = useMemo(() => Object.fromEntries(promoteurs.map(p => [p.id, p])), [promoteurs]);

  const filtered = useMemo(() => {
    return encaissements.filter(e => {
      if (moisFilter !== 'all') {
        const m = Number(e.date.slice(5, 7));
        if (m !== Number(moisFilter)) return false;
      }
      if (modeFilter !== 'all' && e.mode_paiement !== modeFilter) return false;
      if (projetFilter !== 'all') {
        if (projetFilter === '_none' && e.projet_id) return false;
        if (projetFilter !== '_none' && e.projet_id !== projetFilter) return false;
      }
      return true;
    });
  }, [encaissements, moisFilter, modeFilter, projetFilter]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      date: e.date, montant: e.montant, libelle: e.libelle || '',
      mode_paiement: e.mode_paiement || 'virement', facture_id: e.facture_id || '',
      projet_id: e.projet_id || '', promoteur_id: e.promoteur_id || '',
      reference: e.reference || '', description: e.description || '',
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      montant: Number(form.montant),
      facture_id: form.facture_id || null,
      projet_id: form.projet_id || null,
      promoteur_id: form.promoteur_id || null,
    };
    try {
      if (editingId) {
        await api.put(`/encaissements/${editingId}`, payload);
        toast.success('Encaissement mis à jour');
      } else {
        await api.post('/encaissements', payload);
        toast.success('Encaissement enregistré');
      }
      setOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (err) {
      console.error('[Encaissements] submit failed', err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join(' | ')
        : 'Erreur';
      toast.error(msg);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cet encaissement ?')) return;
    await api.delete(`/encaissements/${id}`);
    toast.success('Supprimé');
    load();
  };

  const total = filtered.reduce((s, e) => s + e.montant, 0);

  // Synthèse par mode de paiement
  const byMode = useMemo(() => {
    const map = {};
    for (const e of filtered) {
      map[e.mode_paiement] = (map[e.mode_paiement] || 0) + e.montant;
    }
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [filtered]);

  return (
    <Layout
      title="Encaissements réels"
      subtitle={`Cash inflows — Exercice ${annee}`}
      actions={
        <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
          <SelectTrigger className="w-32 rounded-none border-zinc-900"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      }
    >
      {/* Stats globales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 border-l-4 border-l-emerald-500 p-5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1">Total encaissé {annee}</div>
          <div className="font-mono font-medium text-3xl" data-testid="total-encaissements">{fmtEUR(total)}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1">Nombre d&apos;encaissements</div>
          <div className="font-mono font-medium text-3xl">{filtered.length}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1">Encaissement moyen</div>
          <div className="font-mono font-medium text-3xl">{filtered.length > 0 ? fmtEUR(total / filtered.length) : '—'}</div>
        </div>
      </div>

      {/* Filtres + bouton */}
      <div className="bg-white border border-zinc-200 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Mois</Label>
          <Select value={moisFilter} onValueChange={setMoisFilter}>
            <SelectTrigger className="w-32 rounded-none" data-testid="enc-mois-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Tous</SelectItem>
              {months.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Mode</Label>
          <Select value={modeFilter} onValueChange={setModeFilter}>
            <SelectTrigger className="w-40 rounded-none" data-testid="enc-mode-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Tous</SelectItem>
              {Object.entries(MODE_PAIEMENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Projet</Label>
          <Select value={projetFilter} onValueChange={setProjetFilter}>
            <SelectTrigger className="w-56 rounded-none" data-testid="enc-projet-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none max-h-80">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="_none">— Sans projet —</SelectItem>
              {projets.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" onClick={openCreate} data-testid="nouvel-encaissement-btn">
                <Plus size={14} className="mr-2" /> Encaissement
              </Button>
            </SheetTrigger>
            <SheetContent className="rounded-none w-full sm:max-w-md overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display font-bold text-2xl tracking-tight">{editingId ? 'Modifier l\'encaissement' : 'Nouvel encaissement'}</SheetTitle></SheetHeader>
              <form onSubmit={submit} className="space-y-4 mt-6">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date *</Label>
                    <Input required type="date" className="rounded-none font-mono" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="enc-date-input" />
                  </div>
                  <div>
                    <Label>Montant (€) *</Label>
                    <Input required type="number" step="0.01" className="rounded-none font-mono" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} data-testid="enc-montant-input" />
                  </div>
                </div>
                <div>
                  <Label>Libellé</Label>
                  <Input className="rounded-none" value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} placeholder="Ex: Acompte AXDEV — facture #2025-014" />
                </div>
                <div>
                  <Label>Mode de paiement</Label>
                  <Select value={form.mode_paiement} onValueChange={(v) => setForm({ ...form, mode_paiement: v })}>
                    <SelectTrigger className="rounded-none" data-testid="enc-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {Object.entries(MODE_PAIEMENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Référence</Label>
                  <Input className="rounded-none font-mono text-xs" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="N° virement, chèque, etc." />
                </div>
                <div className="border-t border-zinc-200 pt-3">
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Rattachement (optionnel)</Label>
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label className="text-xs">Projet</Label>
                      <Select value={form.projet_id || '_none'} onValueChange={(v) => setForm({ ...form, projet_id: v === '_none' ? '' : v })}>
                        <SelectTrigger className="rounded-none" data-testid="enc-projet-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none max-h-80">
                          <SelectItem value="_none">— Sans projet —</SelectItem>
                          {projets.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.nom}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Client (si pas de projet)</Label>
                      <Select value={form.promoteur_id || '_none'} onValueChange={(v) => setForm({ ...form, promoteur_id: v === '_none' ? '' : v })}>
                        <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none max-h-80">
                          <SelectItem value="_none">— Aucun —</SelectItem>
                          {promoteurs.map(p => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Facture d&apos;honoraires liée</Label>
                      <Select value={form.facture_id || '_none'} onValueChange={(v) => setForm({ ...form, facture_id: v === '_none' ? '' : v })}>
                        <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none max-h-80">
                          <SelectItem value="_none">— Encaissement libre —</SelectItem>
                          {factures.map(f => <SelectItem key={f.id} value={f.id}>{f.date} · {f.projet_code} · {fmtEUR(f.montant)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input className="rounded-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <Button type="submit" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="enc-submit-btn">
                  {editingId ? 'Mettre à jour' : 'Enregistrer'}
                </Button>
              </form>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Synthèse par mode */}
      {byMode.length > 0 && (
        <div className="bg-white border border-zinc-200 mb-4">
          <div className="border-b border-zinc-900 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium flex items-center gap-2">
              <TrendingUp size={11} /> Synthèse par mode de paiement
            </div>
          </div>
          <div className="p-3 flex flex-wrap gap-2">
            {byMode.map(([mode, sum]) => (
              <div key={mode} className={`border px-3 py-1.5 ${MODE_COLORS[mode] || 'border-zinc-300'}`}>
                <div className="text-[10px] uppercase tracking-wider">{MODE_PAIEMENT_LABELS[mode] || mode}</div>
                <div className="font-mono font-medium text-sm">{fmtEUR(sum)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-zinc-200" data-testid="encaissements-table">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-900">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Date</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Libellé</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Mode</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Projet / Client</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Réf.</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Montant</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const proj = e.projet_id ? projMap[e.projet_id] : null;
              const promo = e.promoteur_id ? promoMap[e.promoteur_id] : null;
              return (
                <tr key={e.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{e.date}</td>
                  <td className="px-4 py-2 font-medium">
                    {e.libelle || '—'}
                    {e.description && <div className="text-[10px] text-zinc-500">{e.description}</div>}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`inline-block px-2 py-0.5 border uppercase tracking-wider text-[10px] ${MODE_COLORS[e.mode_paiement] || 'border-zinc-300'}`}>
                      {MODE_PAIEMENT_LABELS[e.mode_paiement] || e.mode_paiement}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {proj ? <span><span className="font-mono">{proj.code}</span> · <span className="text-zinc-600">{proj.nom}</span></span>
                      : promo ? <span className="text-zinc-600">{promo.nom}</span>
                      : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-500">{e.reference || '—'}</td>
                  <td className="px-4 py-2 font-mono text-right text-emerald-700 font-medium">{fmtEUR(e.montant)}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(e)} className="p-1 hover:bg-zinc-200" data-testid={`edit-enc-${e.id}`}><Pencil size={12} /></button>
                    <button onClick={() => remove(e.id)} className="p-1 hover:bg-rose-100 text-rose-600 ml-1"><Trash2 size={12} /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500">Aucun encaissement.</td></tr>}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
